// NextActionBanner — Persistent banner at the top of TripCardFull showing the
// single most important next action for the traveler.
//
// This is a presentational component that receives the computed next action
// as props (from `nextActionEngine.computeNextAction`). The parent component
// (TripCardFull) handles the 30-second refresh via `useCurrentTime` hook.
//
// Features:
//   - Instruction text ≤80 characters with contextual action button
//   - Idle message when no pending action ("Next step in 2 hours", "Trip complete")
//   - Sticky positioning at top of TripCard regardless of scroll
//   - role="status" and aria-live="polite" for screen reader announcements
//   - Updates within 30 seconds when real-time conditions change (parent tick)
//   - Advances to next action within 5 seconds when current action becomes
//     inapplicable (parent re-computes and passes new props)
//
// Validates: Requirements 1.3, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 12.5

import React from 'react';
import { colors, radii, typography, fontWeights, shadows } from '../../styles/designTokens';

// ── Styles ──────────────────────────────────────────────────

const bannerContainerStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: colors.info,
  color: '#ffffff',
  padding: '12px 16px',
  borderRadius: `${radii.inner} ${radii.inner} 0 0`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  minHeight: '48px',
};

const instructionStyle = {
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.semibold,
  lineHeight: '1.4',
  flex: 1,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const idleInstructionStyle = {
  ...instructionStyle,
  fontWeight: fontWeights.regular,
  opacity: 0.9,
};

const departureTimeStyle = {
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.regular,
  lineHeight: '1.3',
  margin: '4px 0 0 0',
  opacity: 0.9,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  borderRadius: radii.button,
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.semibold,
  border: '2px solid #ffffff',
  backgroundColor: 'transparent',
  color: '#ffffff',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  minWidth: '44px',
  minHeight: '44px',
  transition: 'background-color 0.15s, border-color 0.15s',
  flexShrink: 0,
};

// ── Component ───────────────────────────────────────────────

/**
 * @typedef {Object} ActionButton
 * @property {string} label - Button label text (e.g., "Get Directions", "Call Uber")
 * @property {() => void} onClick - Click handler for the action
 */

/**
 * @typedef {Object} NextActionBannerProps
 * @property {string} instruction - Instruction text ≤80 characters
 * @property {ActionButton} [actionButton] - Optional contextual action button
 * @property {boolean} [isIdle] - Whether the banner is in idle state
 * @property {string} [idleMessage] - Message to display when idle
 * @property {string|null} [departureTime] - Formatted departure time string or null for unavailable (Req 1.2, 1.4, 1.5, 1.6)
 */

/**
 * NextActionBanner displays the single most important next action at the top
 * of the TripCard. It uses sticky positioning to remain visible during scroll
 * and includes role="status" with aria-live="polite" for accessible updates.
 *
 * Wrapped in React.memo to prevent sibling re-renders when Stage_Nodes toggle
 * (Req 11.3). The banner only re-renders when its own props change.
 *
 * @param {NextActionBannerProps} props
 * @returns {React.ReactElement}
 */
export const NextActionBanner = React.memo(function NextActionBanner({ instruction, actionButton, isIdle, idleMessage, departureTime }) {
  const displayText = isIdle ? (idleMessage || instruction) : instruction;

  // When departureTime prop is passed (departure-from-origin stage is active),
  // show the formatted time or "Departure time unavailable" (Req 1.2, 1.4, 1.5, 1.6)
  const departureDisplay = departureTime !== undefined
    ? (departureTime || 'Departure time unavailable')
    : null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="next-action-banner"
      style={bannerContainerStyle}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <p
          data-testid="next-action-instruction"
          style={isIdle ? idleInstructionStyle : instructionStyle}
        >
          {displayText}
        </p>

        {departureDisplay && (
          <p
            data-testid="departure-time-display"
            style={departureTimeStyle}
          >
            {departureDisplay}
          </p>
        )}
      </div>

      {!isIdle && actionButton && (
        <button
          type="button"
          data-testid="next-action-button"
          onClick={actionButton.onClick}
          style={actionButtonStyle}
          aria-label={actionButton.label}
        >
          {actionButton.label}
        </button>
      )}
    </div>
  );
});

export default NextActionBanner;
